import { Head, useForm } from "@inertiajs/react"
import { Upload } from "lucide-react"
import { useRef } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import { csvImportsPath, newCsvImportPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Import CSV", href: newCsvImportPath() },
]

export default function NewCsvImport() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const form = useForm<{ file: File | null }>({ file: null })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    form.setData("file", file)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.data.file) return

    form.post(csvImportsPath(), {
      forceFormData: true,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Import CSV" />

      <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
        <div>
          <h2 className="text-lg font-semibold">Import CSV</h2>
          <p className="text-muted-foreground text-sm">
            Step 1 of 3: Upload your bank statement or expense export
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            className="hover:border-foreground/50 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 text-center transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="text-muted-foreground mb-4 h-12 w-12" />
            {form.data.file ? (
              <div>
                <p className="font-medium">{form.data.file.name}</p>
                <p className="text-muted-foreground text-sm">
                  {(form.data.file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div>
                <p className="mb-1 font-medium">
                  Drop a CSV file here or click to browse
                </p>
                <p className="text-muted-foreground text-sm">
                  Supports bank statements, QuickBooks exports, and similar
                  formats
                </p>
              </div>
            )}
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={!form.data.file || form.processing}>
              {form.processing ? "Parsing..." : "Upload & Map Columns"}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
